import { useEffect, useRef, useState } from 'react';

// High-tier / major trading countries (with emoji flags)
const FLAGS = {
  // South Asia
  BD: '\u{1F1E7}\u{1F1E9}', IN: '\u{1F1EE}\u{1F1F3}', PK: '\u{1F1F5}\u{1F1F0}', NP: '\u{1F1F3}\u{1F1F5}', LK: '\u{1F1F1}\u{1F1F0}', BT: '\u{1F1E7}\u{1F1F9}', MV: '\u{1F1F2}\u{1F1FB}',
  // North America
  US: '\u{1F1FA}\u{1F1F8}', CA: '\u{1F1E8}\u{1F1E6}', MX: '\u{1F1F2}\u{1F1FD}',
  // Europe (G7 + wealthy)
  GB: '\u{1F1EC}\u{1F1E7}', DE: '\u{1F1E9}\u{1F1EA}', FR: '\u{1F1EB}\u{1F1F7}', IT: '\u{1F1EE}\u{1F1F9}', ES: '\u{1F1EA}\u{1F1F8}', NL: '\u{1F1F3}\u{1F1F1}',
  CH: '\u{1F1E8}\u{1F1ED}', SE: '\u{1F1F8}\u{1F1EA}', NO: '\u{1F1F3}\u{1F1F4}', DK: '\u{1F1E9}\u{1F1F0}', FI: '\u{1F1EB}\u{1F1EE}', BE: '\u{1F1E7}\u{1F1EA}',
  AT: '\u{1F1E6}\u{1F1F9}', IE: '\u{1F1EE}\u{1F1EA}', PT: '\u{1F1F5}\u{1F1F9}', PL: '\u{1F1F5}\u{1F1F1}', GR: '\u{1F1EC}\u{1F1F7}',
  // Gulf / MENA (high-income)
  AE: '\u{1F1E6}\u{1F1EA}', SA: '\u{1F1F8}\u{1F1E6}', QA: '\u{1F1F6}\u{1F1E6}', KW: '\u{1F1F0}\u{1F1FC}', OM: '\u{1F1F4}\u{1F1F2}', BH: '\u{1F1E7}\u{1F1ED}',
  TR: '\u{1F1F9}\u{1F1F7}', EG: '\u{1F1EA}\u{1F1EC}', JO: '\u{1F1EF}\u{1F1F4}', IL: '\u{1F1EE}\u{1F1F1}',
  // East & SE Asia
  JP: '\u{1F1EF}\u{1F1F5}', KR: '\u{1F1F0}\u{1F1F7}', CN: '\u{1F1E8}\u{1F1F3}', HK: '\u{1F1ED}\u{1F1F0}', TW: '\u{1F1F9}\u{1F1FC}',
  SG: '\u{1F1F8}\u{1F1EC}', MY: '\u{1F1F2}\u{1F1FE}', ID: '\u{1F1EE}\u{1F1E9}', TH: '\u{1F1F9}\u{1F1ED}', PH: '\u{1F1F5}\u{1F1ED}', VN: '\u{1F1FB}\u{1F1F3}',
  // Oceania
  AU: '\u{1F1E6}\u{1F1FA}', NZ: '\u{1F1F3}\u{1F1FF}',
  // LATAM
  BR: '\u{1F1E7}\u{1F1F7}', AR: '\u{1F1E6}\u{1F1F7}', CL: '\u{1F1E8}\u{1F1F1}', CO: '\u{1F1E8}\u{1F1F4}',
  // Africa
  ZA: '\u{1F1FF}\u{1F1E6}', NG: '\u{1F1F3}\u{1F1EC}', KE: '\u{1F1F0}\u{1F1EA}', MA: '\u{1F1F2}\u{1F1E6}',
  // Others
  RU: '\u{1F1F7}\u{1F1FA}', UA: '\u{1F1FA}\u{1F1E6}',
};

// Ordered pin-list of high-value countries (shown as social proof, always visible)
const PINNED = [
  'US', 'GB', 'CA', 'AU', 'DE', 'FR', 'JP', 'KR', 'SG', 'HK',
  'AE', 'SA', 'QA', 'KW', 'CH', 'NL', 'SE', 'IT', 'ES', 'NZ',
  'BD', 'IN', 'PK', 'MY', 'ID', 'TR', 'BR', 'ZA', 'NG', 'IL',
  'NP', 'LK', 'PH', 'TH', 'VN', 'EG', 'MX', 'CN', 'TW', 'RU',
];

function useCountUp(to, duration = 1400) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    let raf; let start;
    function step(ts) {
      if (!start) start = ts;
      const t = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(to * eased));
      if (t < 1) raf = requestAnimationFrame(step);
    }
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        raf = requestAnimationFrame(step);
        io.disconnect();
      }
    });
    if (ref.current) io.observe(ref.current);
    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, [to, duration]);
  return { val, ref };
}

export default function SocialProofStrip({ stats }) {
  const joined = stats?.total_joined || 131312;
  const { val, ref } = useCountUp(joined);
  const countries = (stats?.by_country || []);

  // Merge: live country data first, then pinned list, dedupe
  const seen = new Set();
  const merged = [];
  for (const c of countries) {
    if (c.country_code && FLAGS[c.country_code] && !seen.has(c.country_code)) {
      merged.push(c.country_code);
      seen.add(c.country_code);
    }
  }
  for (const code of PINNED) {
    if (!seen.has(code) && FLAGS[code]) {
      merged.push(code);
      seen.add(code);
    }
  }

  // Duplicate list for seamless marquee loop
  const loop = [...merged, ...merged];

  return (
    <section className="relative bg-[hsl(240_6%_7%)] border-y border-border overflow-hidden">
      <div className="container-x py-6 sm:py-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div ref={ref} className="flex items-center gap-4 shrink-0">
          <span className="pulse-dot" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(43_92%_56%)]">Live · Updated now</div>
            <div className="font-display uppercase text-3xl sm:text-4xl leading-none mt-1" data-testid="joined-counter-value">
              {val.toLocaleString()}+
            </div>
            <div className="text-xs text-muted-foreground mt-1">Traders joined from {merged.length}+ countries</div>
          </div>
        </div>

        {/* Marquee flag strip */}
        <div className="relative flex-1 min-w-0 overflow-hidden" data-testid="joined-country-flags"
             style={{
               maskImage: 'linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent)',
               WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent)',
             }}>
          <div className="flex items-center gap-2 marquee-track w-max">
            {loop.map((cc, i) => (
              <span
                key={`${cc}-${i}`}
                title={cc}
                className="flex items-center gap-1.5 shrink-0 px-2.5 py-1.5 rounded-lg bg-[hsl(240_6%_10%)] border border-border text-lg"
              >
                <span>{FLAGS[cc]}</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{cc}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
